import { TemplatePortal } from '@angular/cdk/portal';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Subject, takeUntil } from 'rxjs';
import Icons from 'src/app/core/enums/icons.enum';
import { ScreenState } from 'src/app/core/enums/sceen-state.enum';
import { ClientStateService } from 'src/app/core/services/client-state.service';
import { PortalMessagingService } from 'src/app/core/services/portal-messaging.service';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { ScreenStateService } from 'src/app/core/services/screen-state.service';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import { Application } from 'src/portaljs/constants/application';
import { Notification } from 'src/portaljs/models/notification';
import {AppAction} from '../../../portaljs/models/app-action';
import {PortalApp} from '../../core/shared/portal-app';

export enum NotificationFilter {
  ALL = 'ALL',
  UNREAD = 'UNREAD'
}

@Component({
    selector: 'fss-portal-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    standalone: false
})
export class NotificationComponent implements OnDestroy, OnInit {
  private ngUnsubscribe = new Subject();
  public icons = Icons;
  public notificationFilter = NotificationFilter;
  public allNotifications: Notification[] = [];
  public displayableNotifications: Notification[] = []; // ALL or UNREAD, depending on selected NotificationFilter
  public showUnreadOnly: boolean = localStorage.getItem('notificationFilter') === NotificationFilter.UNREAD;
  public unreadCount: number = 0;
  public portal: TemplatePortal<any>;
  public readonly MOBILE_HEADER_TEXT = 'Notifications';

  @ViewChild('menuTrigger', { read: MatMenuTrigger }) public notificationsTrigger: MatMenuTrigger;
  @ViewChild('notificationsTemplate') public notificationsTemplate: TemplateRef<any>;

  public get menuButtonAriaLabel() {
    if (this.unreadCount > 0) {
      return `Notifications. ${this.unreadCount} unread.`;
    }

    return 'Notifications';
  }

  public get isMobile(): boolean {
    return this.toolbarService.isMobile();
  }

  constructor(
    private screenStateService: ScreenStateService,
    private clientStateSvc: ClientStateService,
    private portalStateSvc: PortalStateService,
    private msgSvc: PortalMessagingService,
    private toolbarService: ToolbarService,
    private _viewContainerRef: ViewContainerRef) {
  }

  public ngOnInit(): void {
    this.showUnreadOnly = localStorage.getItem('notificationFilter') === NotificationFilter.UNREAD;

    this.screenStateService.screenState$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state) => {
        if (this.isUnEnabledForCurrentApp()) {
          if (this.notificationsTrigger && this.notificationsTrigger.menuOpen && state === ScreenState.Mobile) {
            this.notificationsTrigger.closeMenu();
            this.showInSidenav();
          }
          if (this.portal && state !== ScreenState.Mobile && this.toolbarService?.portal?.title === this.MOBILE_HEADER_TEXT) {
            this.portal = undefined;
            this.toolbarService.portal = undefined;
            setTimeout(() => this.notificationsTrigger.openMenu());
          }
        }
      });

    this.clientStateSvc.getNotifications$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((notifications: Notification[]) => {
        this.allNotifications = notifications;
        this.refreshVisibleNotifications();
      });
  }

  public isUsingDestinyTheme() {
    const app = this.portalStateSvc.getActiveApp().app;
    if (app !== Application.DD && app !== Application.COLLECTIONS) {
      return true;
    }
    return false;
  }

  public isUnEnabledForCurrentApp(): boolean {
    return this.portalStateSvc.isUnEnabledForCurrentApp();
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public clickNotification(notification: Notification) {
    this.notificationsTrigger.closeMenu();
    this.toolbarService.isMobileMenuOpen = false;
    this.toolbarService.portal = undefined;

    // Create a dummy action to signal not to send default actions to DBO because DBO does not want app action sent when
    // notification is clicked. LCP-432
    const dummyAction: AppAction = {source: Application.PORTAL, destination: Application.PORTAL, route: 'notification-click'};

    const portalApp: PortalApp = this.portalStateSvc.getSelectableApp(notification.source);
    this.portalStateSvc.setActiveApp(portalApp, dummyAction) // Switch to the Notification's application
      .then(() => {
        this.msgSvc.sendNotificationResponse('clicked', notification); // alert embedded application first
        if (notification.new) {
          // only update the notifications if the message was previously unread
          notification.new = false;
          this.unreadCount = this.allNotifications.filter((n) => n.new).length;
          this.clientStateSvc.updateNotification(notification);
        }
      });
  }

  public markAllAsRead() {
    this.allNotifications.forEach((notification) => {
      this.msgSvc.sendNotificationResponse('read', notification); // alert embedded application so it can persist the acknowledgement
      notification.new = false;
      this.clientStateSvc.updateNotification(notification);
    });
  }

  public clickNotificationFilter(filter: NotificationFilter) {
    this.showUnreadOnly = filter === NotificationFilter.UNREAD;
    this.refreshVisibleNotifications();
    localStorage.setItem('notificationFilter', filter);
  }

  public toggleNotificationFilter() {
    this.showUnreadOnly = !this.showUnreadOnly;
    localStorage.setItem('notificationFilter', this.showUnreadOnly ? NotificationFilter.UNREAD : NotificationFilter.ALL);
    const unreadNotifs = this.allNotifications.filter((n) => n.new);
    this.unreadCount = unreadNotifs.length;
    this.displayableNotifications = this.showUnreadOnly ? unreadNotifs : this.allNotifications;
  }

  private refreshVisibleNotifications() {
    const unreadNotifications = this.allNotifications.filter((n) => n.new);
    this.unreadCount = unreadNotifications.length;

    if (!this.showUnreadOnly || (this.showUnreadOnly && this.unreadCount === 0)) {
      this.displayableNotifications = this.allNotifications;
    } else {
      this.displayableNotifications = unreadNotifications;
    }
  }

  public getSourceDescription(notification: Notification) {
    if (notification.source === Application.DESTINY) {
      return 'Destiny Back Office';
    } else if (notification.source === Application.CLM) {
      return 'Classroom Library';
    } else {
      return notification.source;
    }
  }

  public closeSidenav() {
    this.toolbarService.closeSidenavOnMenuOpen();
  }

  public showInSidenav() {
    if (this.isUnEnabledForCurrentApp() && this.isMobile) {
      this.portal = new TemplatePortal(this.notificationsTemplate, this._viewContainerRef);
      this.toolbarService.portal = {templatePortal: this.portal, title: this.MOBILE_HEADER_TEXT};
    }
  }

  public getNotificationIcon(notification: Notification) {
    if (notification.source === Application.CLM) {
      return this.icons.CLM;
    }
    return this.icons.DESTINY;
  }
}
